import * as _ from 'lodash'
import {IBusinessObject, IBusinessObjectChanges, IBusinessObjectChangesOptions} from '../PA.Core/PA.Core.IF.BO';
import {IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {WB_PolicyAreaRow, WB_PolicyTermTypeRow} from "../PA.Core/PAdbSchemaRows";

import {
    esWB_Policy_Term_Type,
    esWB_PolicyArea
} from "../PA.Core/PAdbSchema";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import {ASync} from "../PA.Core/Basic/ASync";
import {RecordUtils} from "../PA.Core/Basic/Records";
import {DefaultChanges} from "../PA.Core/BusinessObject/BusinessObjectChanges";
import {BehaviorSubject} from "rxjs";
import {PolicyBOValidations} from "./PolicyBO.Validations";
import {PolicyAreaBOValidations} from "./PolicyAreaBO.Validations";


export class PolicyAreaBO implements IBusinessObject {
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_PolicyArea: WB_PolicyAreaRow;

    constructor(options?: any) {

        this.CreateOptions = options || {};

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New Policy Area'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_PolicyArea = null;

        this.PolicyTermType_init()

    }

    get WB_PolicyArea(): WB_PolicyAreaRow {
        return this.drWB_PolicyArea;
    }


    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        var self = this;
        this.LoadProgressProvider.start("Creating Policy Area");
        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            self.LoadOptions = options || {};
            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                var sError = "Unable to determine dbContext for new Policy Area.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            // Create record using dbContext
            this.dbContext.createRecordWithKey(esWB_PolicyArea)
                .then((newPolicyAreaRow: WB_PolicyAreaRow) => {
                    newPolicyAreaRow.setDefaults()
                    self.drWB_PolicyArea = newPolicyAreaRow;
                    self.dbContext.add(esWB_PolicyArea, newPolicyAreaRow)
                    self.updateIdentity();
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");

                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    resolve(self);

                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err)
                    error(err)
                })

        });

        return this._OnLoadComplete;
    }

    async Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        const self = this;
        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start('Loading (' + self.Identity.id_ui + ')');
        let olc = this._OnLoadComplete = (async () => {
            try {
                if (self.bIsLoaded) {
                    const sError = 'Policy Area already loaded.';
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(sError);
                    return self;
                }
                self.LoadOptions = options || {};

                if ('dbContext' in self.LoadOptions) {
                    self.dbContext = self.LoadOptions.dbContext;
                } else if (_.isEmpty(self.dbContext)) {
                    self.dbContext = new PAdbContext();
                }

                if (_.isEmpty(this.dbContext)) {
                    const sError = 'Unable to determine dbContext for Policy Area.';
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(sError);
                    return self;
                }

                if ('RecordResolver' in self.LoadOptions) {
                    recordResolver = (<any>self.LoadOptions).RecordResolver;
                    recordResolverLoadPromise = ASync.resolve();
                } else {
                    recordResolver = PolicyAreaBO.createRecordResolver({dbContext: self.dbContext});
                    recordResolverLoadPromise = PolicyAreaBO.setupRecordResolver(recordResolver, [anId]);
                }

                await recordResolverLoadPromise

                const aPolicyArea: any = recordResolver.get(esWB_PolicyArea, anId);

                if (_.isEmpty(aPolicyArea)) {
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception('Id not found.');
                    return self;
                }

                self.drWB_PolicyArea = aPolicyArea;
                self.updateIdentity();

                self._bIsLoaded = true;
                self._OnLoadComplete = null;
                self.LoadProgressProvider.finish();
                return self
            } catch (err: any) {
                self._OnLoadComplete = undefined;
                self.LoadProgressProvider.exception(err);
                return self;
            }
        })();

        return olc;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }


    async Validate(options?: any): Promise<IBusinessObject> {
        const self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start(
            'Validating ' + self.WB_PolicyArea.cif.POLICYAREA_NAME + '(' + self.WB_PolicyArea.cif.POLICYAREA_ID + ')'
        )

        self.ValidateOptions = options || {};

        try {
            if (self.dbContext == undefined) {
                const sError = 'Unable to determine dbContext to validate Policy Area.';
                self.ValidateProgressProvider.exception(sError);
                throw sError;
            }

            await PolicyAreaBOValidations.ValidateFields(self)


            if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                self._bIsValidated = false;
            } else {
                self._bIsValidated = true;
            }
            self.ValidateProgressProvider.finish();
            return self

        } catch (err) {
            self._bIsValidated = false;
            self.ValidateProgressProvider.exception(err);

        }
        return self

    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }


    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {

        const self = this;
        this.SaveProgressProvider.start(
            'Saving ' + self.WB_PolicyArea.cif.POLICYAREA_NAME + '(' + self.WB_PolicyArea.cif.PERIOD_TYPE_ID + ')'
        );

        try {
            if (_.isEmpty(self.dbContext)) {
                self._bIsSaved = false;
                const sError = 'Unable to determine dbContext to Save Policy Area.';
                self.SaveProgressProvider.exception(sError);
                return self;
            }

            self.SaveOptions = options || {};

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;

            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            if (!options?.bSkipValidations) {
                await self.Validate({})

                if (!self.bIsValidated) {
                    self._bIsSaved = false;
                    self.SaveProgressProvider.finish();
                    return self
                }
            }

            await saveFunction(esWB_PolicyArea, self.WB_PolicyArea, {})
            await saveFunction(esWB_Policy_Term_Type, self.WB_PolicyArea.WB_PolicyTermTypeRows, {})

            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                });
            } else {
                self._bIsSaved = true;
            }

            self.SaveProgressProvider.finish();
        } catch (err) {
            self.SaveProgressProvider.exception(err);

        } finally {

        }
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }


//Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        let self = this;

        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + self.WB_PolicyArea.cif.POLICYAREA_NAME + "(" + self.WB_PolicyArea.cif.POLICYAREA_ID + ")");
        return new Promise<IBusinessObject>((resolve, error) => {

            // 1. Validate if we can delete the PeriodType

            // 2. If Valid, mark all the records for deletion.
            self.WB_PolicyArea.markDeleted()
            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);

                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err)
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }


    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    //</editor-fold>
    private updateIdentity(): void {
        const {
            WB_POLICYAREA_ID,
            POLICYAREA_ID,
            POLICYAREA_NAME
        } = this.WB_PolicyArea.cif;
        this.Identity.setAll(WB_POLICYAREA_ID, POLICYAREA_ID, POLICYAREA_NAME);
        this.LoadProgressProvider.setName(
            'Loading ' + this.Identity.name + '(' + this.Identity.id_ui + ')'
        );
        this.Identity.listenToRecordUpdates(this.drWB_PolicyArea, 'WB_POLICYAREA_ID', 'POLICYAREA_ID', 'POLICYAREA_NAME');
        this.Identity.enableRecordWriteBack(this.drWB_PolicyArea, 'POLICYAREA_ID', 'POLICYAREA_NAME')

    }

    //</editor-fold>

    //<editor-fold desc = "Policy Term Type operations">

    _OnPolicyTermTypesChanged: BehaviorSubject<PolicyAreaBO>
    PolicyTermType_init(){
        this._OnPolicyTermTypesChanged = new BehaviorSubject<PolicyAreaBO>(this)
    }

    get OnPolicyTermTypesChanged(): BehaviorSubject<PolicyAreaBO> {
        return this._OnPolicyTermTypesChanged
    }

    async addPolicyTermType(): Promise<WB_PolicyTermTypeRow> {
        const self = this
        let newRow = <WB_PolicyTermTypeRow> await self.dbContext.createRecordWithKey(esWB_Policy_Term_Type, {})
        newRow.setDefaults()
        newRow.cif.WB_POLICYAREA_ID = self.WB_PolicyArea.cif.WB_POLICYAREA_ID
        self.dbContext.add(esWB_Policy_Term_Type, newRow)

        self.OnPolicyTermTypesChanged.next(self)
        return newRow
    }

    async removePolicyTermType(wbPolicyTermMId: number): Promise<void> {
        const self = this
        
        _.forEach(self.WB_PolicyArea.WB_PolicyTermTypeRows, (a) => {
            if(a.cif.WB_POLICYTERMM_ID == wbPolicyTermMId)
                a.markDeleted()
        })

        self.OnPolicyTermTypesChanged.next(self)
    }

    //</editor-fold>

    //<editor-fold desc = "staticIRecordResolver">

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        const opts = boLoadOptions || {};
        let dbContext: PAdbContext;
        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const period_type_options: CreateOptions = {
            standardConditions: {
                [esWB_PolicyArea]: {DELETED_IND: {neq: '1'}}
            },
            dctIncludeBelongsToNamedRelations: {}
        };

        return new ESRecordResolver(dbContext, period_type_options);
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: (string | number)[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            recordResolver.findAllWithRelationships(esWB_PolicyArea, ids)
                .then(() => {
                    resolve();
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }


    //</editor-fold>

};
PAEnvironment.registerBusinessObjectClass(PolicyAreaBO, "PolicyAreaBO", "Policy Area")
